import { useMemo, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { groupItemsBySite, fmtEUR } from "./InvoiceCommon";

const sum = (its, f) => its.reduce((a, { it }) => a + f(it), 0);
const ht = (it) => (Number(it.quantity) || 0) * (Number(it.unit_price_ht) || 0);
const ttc = (it) => ht(it) * (1 + (Number(it.vat_rate) || 0) / 100);

export default function SplitBySiteDialog({ devis, onClose, onDone }) {
  const groups = groupItemsBySite(devis);
  const siteGroups = groups.filter((g) => g.site?.site_id);
  const others = groups.find((g) => g.site && !g.site.site_id)?.items || [];
  const [othersMode, setOthersMode] = useState("separate");
  const [pcts, setPcts] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const rows = useMemo(() => {
    const r = siteGroups.map((g) => ({ key: g.site.site_id, name: g.site.site_name, address: g.site.site_address,
      items: othersMode === g.site.site_id ? [...g.items, ...others] : g.items }));
    if (others.length && othersMode === "separate") r.push({ key: "others", name: "Autres prestations", address: "", items: others });
    return r.filter((x) => x.items.length);
  }, [othersMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await http.post(`/devis/${devis.id}/to-factures-by-site`, { others_mode: othersMode, deposit_percents: pcts });
      setDone(r.data.factures); onDone();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-auto border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="split-dialog">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-lg font-bold">1 facture par site — {devis.number}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">✕</button>
        </div>
        {done ? <SplitDone factures={done} onClose={onClose} /> : (
          <div className="p-5 space-y-4">
            <p className="text-xs text-slate-600">Une facture sera créée pour chaque site avec uniquement ses lignes. Indiquez si besoin le taux d'acompte à appliquer sur chaque facture.</p>
            {devis.split_invoiced?.length > 0 && (
              <div data-testid="split-already-warning" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                Attention : ce devis a déjà été facturé par site ({devis.split_invoiced.join(", ")}). Continuer créera de nouvelles factures.
              </div>
            )}
            {others.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
                <div className="text-xs font-bold text-amber-900 mb-1.5">{others.length} ligne(s) « Autres prestations » (sans site)</div>
                <select data-testid="split-others-mode" value={othersMode} onChange={(e) => setOthersMode(e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white">
                  <option value="separate">Les facturer sur une facture séparée</option>
                  {siteGroups.map((g) => <option key={g.site.site_id} value={g.site.site_id}>Les ajouter à la facture de « {g.site.site_name} »</option>)}
                </select>
              </div>
            )}
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.key} className="flex items-center gap-3 rounded-md border border-slate-200 border-l-4 px-3 py-2.5" style={{ borderLeftColor: "var(--brand-primary)" }} data-testid={`split-row-${r.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900">{r.name}</div>
                    {r.address && <div className="text-[11px] text-slate-500 truncate">{String(r.address).replace(/\n/g, " · ")}</div>}
                    <div className="text-[11px] text-slate-600 mt-0.5">{r.items.length} ligne(s) · <span className="font-mono">{fmtEUR(sum(r.items, ht))} HT</span> · <span className="font-mono font-semibold">{fmtEUR(sum(r.items, ttc))} TTC</span></div>
                  </div>
                  <label className="text-right shrink-0">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Acompte %</div>
                    <input data-testid={`split-pct-${r.key}`} type="number" min="0" max="100" step="1" value={pcts[r.key] ?? ""} placeholder="0"
                      onChange={(e) => setPcts({ ...pcts, [r.key]: parseFloat(e.target.value) || 0 })}
                      className="w-20 text-right font-mono text-sm border border-slate-300 rounded px-2 py-1" />
                    {pcts[r.key] > 0 && <div className="text-[10px] text-amber-700 font-mono mt-0.5">{fmtEUR(sum(r.items, ttc) * pcts[r.key] / 100)} TTC</div>}
                  </label>
                </div>
              ))}
            </div>
            {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
              <button data-testid="split-submit" disabled={busy || !rows.length} onClick={submit} className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-60">
                {busy ? "Création…" : `Créer ${rows.length} facture(s)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SplitDone({ factures, onClose }) {
  return (
    <div className="p-5" data-testid="split-done">
      <div className="text-sm font-semibold text-emerald-700 mb-3">{factures.length} facture(s) créée(s) :</div>
      <ul className="space-y-1.5 mb-4">
        {factures.map((f) => (
          <li key={f.id} className="flex justify-between text-sm border-b border-slate-100 pb-1.5">
            <span><span className="font-mono font-semibold">{f.number}</span> · {f.site_name}</span>
            <span className="font-mono font-bold">{fmtEUR(f.total_ttc)}</span>
          </li>
        ))}
      </ul>
      <div className="flex justify-end"><button onClick={onClose} className="px-4 py-2 text-sm font-semibold bg-slate-900 text-white rounded-md">Fermer</button></div>
    </div>
  );
}
